import { useState, useEffect, useRef, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { fetchMagnumMoment } from '@/lib/magnumMoment'
import { timArt, TimPose } from '@/lib/timArt'
import DemoGame from '@/components/DemoGame'

// Tim's voice: see TIM_CHARACTER.md. Guardian of the Stream, reassigned to the
// screen by clerical error, fully committed to the post.
const CLICK_QUIPS = [
  "Yes? State your business. I am guarding this screen.",
  "You clicked on me. Bold. I respect it.",
  "I'm Tim. I guarded a river for three thousand years. Now I guard this. It's going fine.",
  "A clerical error assigned me here. I have chosen to regard it as a promotion.",
  "Everything on a screen is streaming. I am a stream guardian. In a sense, nothing has changed.",
  "The stream flows for those who read. That is not a threat. It is policy.",
  "You've never heard of me? Astonishing. I'm somewhat of a legend where I'm from.",
  "Sign up, and I shall say more magnificent things. I have several prepared.",
  "I take this job extremely seriously. Someone must. It may as well be someone majestic.",
  "I am very busy standing guard. But for you, I have a moment.",
]

function pickRandom<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)]
}

export default function Landing() {
  const [quip, setQuip] = useState('')
  // Poking the guardian earns the look he reserves for people who ask what he is.
  const [pose, setPose] = useState<TimPose>('neutral')
  const usedClickQuips = useRef<Set<string>>(new Set())

  useEffect(() => {
    fetchMagnumMoment({ contextType: 'LANDING', streak: 0 })
      .then((m) => {
        if (m.line) setQuip(m.line.text)
      })
      .catch(() => setQuip("I am Tim, Guardian of the Screen. Click me. I have decrees."))
  }, [])

  const handleMagnumClick = useCallback(() => {
    const unused = CLICK_QUIPS.filter(q => !usedClickQuips.current.has(q))
    const pick = unused.length > 0 ? pickRandom(unused) : pickRandom(CLICK_QUIPS)
    usedClickQuips.current.add(pick)
    setQuip(pick)
    setPose('withering')
  }, [])

  const handleDemoQuip = useCallback((text: string) => {
    setQuip(text)
  }, [])

  return (
    <div className="min-h-screen flex flex-col items-center bg-background">
      <div className="w-full max-w-lg p-8 space-y-8">
        <div className="text-center space-y-6">
          <div className="flex items-end justify-center gap-1">
            <button onClick={handleMagnumClick} className="focus:outline-none active:scale-95 transition-transform">
              <img
                src={timArt(pose)}
                alt="Tim"
                className="w-32 h-32 object-contain magnum-character cursor-pointer"
              />
            </button>
            {quip && (
              <div key={quip} className="relative animate-in fade-in slide-in-from-left-2 duration-300">
                <div className="relative bg-card border-2 border-border rounded-2xl rounded-bl-none px-4 py-3 max-w-xs shadow-sm text-left">
                  <p className="text-sm sm:text-base font-medium">{quip}</p>
                </div>
                <div className="absolute bottom-0 -left-[8px] w-0 h-0 border-t-[8px] border-r-[8px] border-t-transparent border-r-border" />
                <div className="absolute bottom-[2px] -left-[4px] w-0 h-0 border-t-[6px] border-r-[6px] border-t-transparent border-r-card" />
              </div>
            )}
          </div>
          <h1 className="font-display text-6xl font-bold tracking-tight text-primary">
            <span className="text-gold">S</span>creen <span className="text-gold">T</span>im
          </h1>
          <p className="font-serif italic text-lg text-muted-foreground">
            Adaptive reading and music theory exercises for kids (and curious adults).
          </p>
          <div className="flex gap-3 justify-center">
            <Link
              to="/login"
              className="px-8 py-4 bg-primary text-primary-foreground rounded-lg text-lg font-bold hover:opacity-90 transition inline-block"
            >
              Log In
            </Link>
            <Link
              to="/signup"
              className="px-8 py-4 border-2 border-primary text-primary rounded-lg text-lg font-bold hover:bg-primary/5 transition inline-block"
            >
              Sign Up
            </Link>
          </div>
        </div>

        <div className="pt-2">
          <div className="flex items-center gap-4 text-gold" aria-hidden="true">
            <div className="flex-1 border-t border-border" />
            <span className="text-xl leading-none select-none">❦</span>
            <div className="flex-1 border-t border-border" />
          </div>
          <div className="pt-8">
            <DemoGame onQuip={handleDemoQuip} />
          </div>
        </div>
      </div>
    </div>
  )
}
